import { SUPABASE_URL } from '../utils/supabase/url';
/** RAMS service — wraps the H&S RAMS edge-function endpoints. Mirrors DayworkService.ts. */

import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/supabase/client';
import type {
  RamsRecord,
  CreateRamsInput,
  UpdateRamsInput,
  TransitionInput,
  InstantiateFromTemplateInput,
  SubmitForReviewInput,
} from '../types/healthSafety';
import type { RamsProjectContext } from '../features/hs/ramsUtils';
import { normaliseRamsStatus } from '../features/hs/ramsUtils';
import { COSHHDocumentsService } from './COSHHDocumentsService';
import { NativeBridge } from './NativeBridge';

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b`;
const RAMS_LIST_CACHE_MS = 30_000;

interface RamsListOptions {
  status?: string;
  workspaceId?: string;
  force?: boolean;
}

interface RamsListCacheEntry {
  records: RamsRecord[];
  expiresAt: number;
}

const ramsListCache = new Map<string, RamsListCacheEntry>();
const ramsListRequests = new Map<string, Promise<RamsRecord[]>>();

function ramsListKey(projectId: string, workspaceId?: string, status?: string): string {
  return `${workspaceId || 'unscoped'}:${projectId}:${status || 'all'}`;
}

function invalidateProjectLists(projectId: string): void {
  for (const key of ramsListCache.keys()) {
    if (key.includes(`:${projectId}:`)) ramsListCache.delete(key);
  }
}

async function authedFetch(url: string, init?: RequestInit): Promise<Response> {
  const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
  const res = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Request failed (${res.status})`);
  }
  return res;
}

/** Read-time belt-and-braces: normalise every record's status before it
 *  reaches a caller, same as normaliseSeverity for hazards. See
 *  ramsUtils.normaliseRamsStatus for what this catches. */
function normaliseRecord(record: RamsRecord): RamsRecord {
  return {
    ...record,
    status: normaliseRamsStatus(record.status),
    documentType: record.arrangements?.documentType ?? record.documentType,
    liftingPlan: record.arrangements?.liftingPlan ?? record.liftingPlan,
  };
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to prepare RAMS pack for download'));
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Failed to prepare RAMS pack for download'));
        return;
      }
      resolve(reader.result.split(',', 2)[1] ?? '');
    };
    reader.readAsDataURL(blob);
  });
}

export const RAMSService = {
  /** For a RAMS saved outside this service, such as one QuantisAI drafted. */
  invalidateProject(projectId: string): void {
    invalidateProjectLists(projectId);
  },

  peekList(projectId: string, workspaceId?: string, status?: string): RamsRecord[] | undefined {
    const key = ramsListKey(projectId, workspaceId, status);
    const cached = ramsListCache.get(key);
    if (!cached) return undefined;
    if (cached.expiresAt <= Date.now()) {
      ramsListCache.delete(key);
      return undefined;
    }
    return cached.records;
  },

  async list(projectId: string, options: RamsListOptions = {}): Promise<RamsRecord[]> {
    const { status, workspaceId, force = false } = options;
    const key = ramsListKey(projectId, workspaceId, status);
    const activeRequest = ramsListRequests.get(key);
    if (activeRequest) return activeRequest;

    if (!force) {
      const cached = RAMSService.peekList(projectId, workspaceId, status);
      if (cached) return cached;
    }

    const request = (async () => {
      const url = new URL(`${API_BASE}/projects/${encodeURIComponent(projectId)}/hs/rams`);
      if (status) url.searchParams.set('status', status);
      const res = await authedFetch(url.toString());
      const data = await res.json() as { rams: RamsRecord[] };
      const records = (data.rams ?? []).map(normaliseRecord);
      if (records.some(record => record.projectId !== projectId)) {
        // Fail closed if a backend regression ever returns a mixed-project
        // payload. Rendering none is safer than filtering silently because the
        // error remains visible to monitoring and cannot expose another tenant.
        throw new Error('RAMS response was outside the requested project scope');
      }
      ramsListCache.set(key, {
        records,
        expiresAt: Date.now() + RAMS_LIST_CACHE_MS,
      });
      return records;
    })();

    ramsListRequests.set(key, request);
    try {
      return await request;
    } finally {
      if (ramsListRequests.get(key) === request) ramsListRequests.delete(key);
    }
  },

  prefetchList(projectId: string, workspaceId: string): Promise<RamsRecord[]> {
    return RAMSService.list(projectId, { workspaceId });
  },

  async get(projectId: string, id: string): Promise<RamsRecord> {
    const res = await authedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/hs/rams/${encodeURIComponent(id)}`,
    );
    const data = await res.json() as { rams: RamsRecord };
    return normaliseRecord(data.rams);
  },

  async create(projectId: string, input: CreateRamsInput): Promise<RamsRecord> {
    const res = await authedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/hs/rams`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    const data = await res.json() as { rams: RamsRecord };
    invalidateProjectLists(projectId);
    return normaliseRecord(data.rams);
  },

  async update(projectId: string, id: string, patch: UpdateRamsInput): Promise<RamsRecord> {
    const res = await authedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/hs/rams/${encodeURIComponent(id)}`,
      { method: 'PUT', body: JSON.stringify(patch) },
    );
    const data = await res.json() as { rams: RamsRecord };
    invalidateProjectLists(projectId);
    return normaliseRecord(data.rams);
  },

  async transition(projectId: string, id: string, input: TransitionInput): Promise<RamsRecord> {
    const res = await authedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/hs/rams/${encodeURIComponent(id)}/transition`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    const data = await res.json() as { rams: RamsRecord };
    invalidateProjectLists(projectId);
    return normaliseRecord(data.rams);
  },

  /** draft -> submitted. Mints one rams_reviews token per reviewer server-side
   *  and emails each a review link — see hs_rams_routes.tsx's transition
   *  handler. Thin wrapper over transition() so callers don't have to know
   *  'submitted' needs extra body fields the other transitions don't. */
  async submitForReview(projectId: string, id: string, input: SubmitForReviewInput): Promise<RamsRecord> {
    return RAMSService.transition(projectId, id, { to: 'submitted', ...input });
  },

  async remove(projectId: string, id: string): Promise<void> {
    await authedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/hs/rams/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    invalidateProjectLists(projectId);
  },

  async fromTemplate(projectId: string, input: InstantiateFromTemplateInput): Promise<RamsRecord> {
    const res = await authedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/hs/rams/from-template`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    const data = await res.json() as { rams: RamsRecord };
    invalidateProjectLists(projectId);
    return normaliseRecord(data.rams);
  },

  /** Clones the record to a fresh draft at version + 1; the issued revision
   *  this was called on is left untouched. */
  async newVersion(projectId: string, id: string): Promise<RamsRecord> {
    const res = await authedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/hs/rams/${encodeURIComponent(id)}/new-version`,
      { method: 'POST' },
    );
    const data = await res.json() as { rams: RamsRecord };
    invalidateProjectLists(projectId);
    return normaliseRecord(data.rams);
  },

  /** Admin/internal symmetry with the public review endpoints — the public
   *  review page itself (features/rams-review/PublicRAMSReviewPage.tsx) does
   *  its own unauthenticated fetch with the anon key, exactly like
   *  PublicApprovalPage.tsx, since a reviewer never has a Quantis session. */
  async getByReviewToken(token: string): Promise<{ rams: RamsRecord; review: unknown; project: unknown }> {
    const res = await authedFetch(`${API_BASE}/rams-review/${encodeURIComponent(token)}`);
    return res.json();
  },

  async respondToReview(
    token: string,
    input: { action: 'approve' | 'decline' | 'changes_requested'; respondedBy?: string; comment?: string },
  ): Promise<{ ok: boolean }> {
    const res = await authedFetch(
      `${API_BASE}/rams-review/${encodeURIComponent(token)}/respond`,
      { method: 'POST', body: JSON.stringify(input) },
    );
    return res.json();
  },

  /** Builds the client deliverable as editable Word RAMS and COSHH documents
   * plus the assigned controlled COSHH PDFs. The ZIP keeps the Word documents
   * and source attachments together. */
  async downloadPack(
    projectId: string,
    id: string,
    projectContext: RamsProjectContext,
  ): Promise<{ attachmentCount: number }> {
    const record = await RAMSService.get(projectId, id);
    const { generateRamsDocx } = await import('../features/hs/ramsDocxExport');
    const { generateCoshhDocx } = await import('../features/hs/coshhDocxExport');
    const { ramsDocRef } = await import('../features/hs/ramsUtils');
    const { default: JSZip } = await import('jszip');
    const ramsDocx = await generateRamsDocx({ rams: record, projectContext });
    const coshhDocx = await generateCoshhDocx({ rams: record, projectContext });
    const documents = Array.from(
      new Map(
        (record.coshhDocuments ?? []).map((assignment) => [assignment.document.id, assignment.document]),
      ).values(),
    );
    const attachments = await Promise.all(documents.map(async (document) => ({
      document,
      pdf: await COSHHDocumentsService.loadPdf(document),
    })));

    const zip = new JSZip();
    const reference = ramsDocRef(record);
    zip.file(`${reference}-Rev${record.version}-RAMS.docx`, ramsDocx);
    const coshhFolder = zip.folder('COSHH');
    coshhFolder?.file(`${reference}-Rev${record.version}-COSHH-Assessment.docx`, coshhDocx);
    const usedNames = new Map<string, number>();
    attachments.forEach(({ document, pdf }, index) => {
      const baseName = document.title.replace(/[<>:"/\\|?*]+/g, '_').replace(/\s+/g, ' ').trim();
      const stem = baseName || `COSHH-${index + 1}`;
      const occurrence = usedNames.get(stem) ?? 0;
      usedNames.set(stem, occurrence + 1);
      const fileName = `${stem}${occurrence > 0 ? `-${occurrence + 1}` : ''}.pdf`;
      coshhFolder?.file(fileName, pdf);
    });
    const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
    const slug = record.taskDescription
      .slice(0, 40)
      .trim()
      .replace(/[<>:"/\\|?*]+/g, '-')
      .replace(/\s+/g, '-');
    const filename = `${reference}-Rev${record.version}-${slug}-RAMS-pack.zip`;
    const base64Data = await blobToBase64(blob);
    await NativeBridge.saveFile(base64Data, filename, 'application/zip');
    return { attachmentCount: attachments.length };
  },

  /** Backwards-compatible alias for native/web callers that still dispatch
   * the original Download PDF action. It now downloads the complete pack. */
  async downloadPdf(
    projectId: string,
    id: string,
    projectContext: RamsProjectContext,
  ): Promise<void> {
    await RAMSService.downloadPack(projectId, id, projectContext);
  },
};
